import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack, Paper, Link, Card, CardContent, Grid } from '@mui/material';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useABTest, useABTestConversion } from '../../../hooks/useABTest';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import BoltIcon from '@mui/icons-material/Bolt';
import { landingThemeConfig } from '../styles/landingThemeConfig';
import { getDemoContainerProps } from '../styles/demoContainerStyles';
// addOpacity imported from colorUtils but now used in theme config
import ExamGeneratorDemo from './ExamGeneratorDemo';
import EmailCaptureModal from './EmailCaptureModal';

// Debug mode - set to true to show layout debug lines
const DEBUG_MODE = false;

const HeroWithDemo = ({ colorTheme = 'teal' }) => {
  const { trackEvent } = useAnalytics();
  const { trackConversion } = useABTestConversion();
  const studentTheme = landingThemeConfig.student(colorTheme);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // A/B testing for headline
  const { variant: headlineVariant } = useABTest('landing_headline', [
    'variant_a', // Benefit-oriented
    'variant_b'  // Pain-point-oriented
  ]);

  // A/B testing for CTA button text
  const { variant: ctaVariant } = useABTest('landing_cta', [
    'variant_a', // Get Early Access
    'variant_b'  // Join the Student Waitlist
  ]);

  const getHeadlineText = () => {
    if (headlineVariant === 'variant_b') {
      return "Tired of hunting for practice exams? Generate custom HSC maths papers in seconds.";
    }
    return "Ace your HSC Maths Exams with AI-Powered Practice.";
  };

  const getCTAText = () => {
    if (ctaVariant === 'variant_b') {
      return "Join the Student Waitlist";
    }
    return "Get Early Access";
  };

  const handleCTAClick = () => {
    trackEvent('cta_click', {
      source: 'hero',
      headline_variant: headlineVariant,
      cta_variant: ctaVariant
    });
    trackConversion('landing_headline', headlineVariant, 'cta_click');
    trackConversion('landing_cta', ctaVariant, 'click');
    setEmailModalOpen(true);
  };

  const handleDemoClick = () => {
    trackEvent('demo_started', {
      source: 'hero_secondary_link'
    });
    // Scroll to demo section smoothly
    const demoElement = document.getElementById('demo-section');
    if (demoElement) {
      demoElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Helper function to render demo section
  const renderDemo = () => {
    // Get centralized demo container styling for landing page
    const demoContainerProps = getDemoContainerProps({ 
      mode: 'landing', 
      urlText: 'demo.examr.ai',
      colorTheme: colorTheme
    });

    return (
      <Box sx={{
        ...demoContainerProps.outerWrapper,
        maxWidth: { xs: '95%', sm: '90%', md: '1200px' }, // Override to match content width
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...(DEBUG_MODE && {
          // DEBUG: Demo outer wrapper
          border: '2px solid gold',
        }),
      }}>
        <Paper
          elevation={0}
          sx={{
            ...demoContainerProps.demoContainer,
            width: '100%', // Ensure it takes full width of wrapper
            margin: '0 auto', // Center horizontally
          }}
        >
          {/* Demo header bar */}
          <Box sx={demoContainerProps.windowHeader}>
            <Stack direction="row" spacing={1}>
              <Box sx={demoContainerProps.windowButtons.red} />
              <Box sx={demoContainerProps.windowButtons.yellow} />
              <Box sx={demoContainerProps.windowButtons.green} />
            </Stack>
            <Typography 
              variant="caption" 
              sx={demoContainerProps.windowUrl}
            >
              {demoContainerProps.urlText}
            </Typography>
          </Box>

          {/* Demo content */}
          <Box sx={{
            ...demoContainerProps.demoContent,
            // Allow full height and scrolling for embedded views
            display: 'flex',
            flexDirection: 'column',
            // Override alignment on mobile to allow proper scrolling
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: { xs: 'flex-start', md: 'center' }
          }}>
            <ExamGeneratorDemo onCTAClick={handleCTAClick} />
          </Box>

          {/* Glow effect - DISABLED for testing */}
          {/* <Box sx={demoContainerProps.glowEffect} /> */}
        </Paper>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: 'transparent', // No background - let gradient show through
      }}
    >
      {/* DEBUG: Center line */}
      {DEBUG_MODE && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '2px',
            height: '100vh',
            background: 'white',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Background glow removed - now handled by StudentLandingPage gradient background */}
      
      <Stack spacing={{ xs: 4, sm: 5, md: 6 }} sx={{ 
        position: 'relative', 
        zIndex: 1, 
        pt: { xs: 12, sm: 14, md: 16 }, // Increased top padding to avoid cramping with nav
        ...(DEBUG_MODE && {
          // DEBUG: Stack outline
          border: '2px solid magenta',
        }),
      }}>
        {/* Title and subtitle section - contained */}
        <Container maxWidth="xl" sx={{ 
          ...(DEBUG_MODE && {
            // DEBUG: Title container
            border: '2px solid cyan',
            position: 'relative',
            '&::before': {
              content: '"TITLE CONTAINER"',
              position: 'absolute',
              top: 0,
              left: 0,
              background: 'cyan',
              color: 'black',
              padding: '2px 8px',
              fontSize: '12px',
              zIndex: 10,
            }
          })
        }}>
          <Box textAlign="center" maxWidth="900px" mx="auto" sx={{
            ...(DEBUG_MODE && {
              // DEBUG: Title inner box
              border: '2px solid yellow',
              position: 'relative',
            })
          }}>
            <Typography
              variant="h1"
              sx={{
                mb: 5, // Increased from 3 to 5
                background: `linear-gradient(135deg, ${studentTheme.components.hero.titleGradientStart} 0%, ${studentTheme.components.hero.titleGradientEnd} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {getHeadlineText()}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: '1.25rem', lineHeight: 1.6, maxWidth: '700px', mx: 'auto' }}
            >
              Generate instant custom topic-specific HSC exams.
              <br />
              Made from thousands of real past HSC questions.
              <br />
              Stop searching - start studying.
            </Typography>
          </Box>
        </Container>

        {/* CTA section - moved above demo */}
        <Container maxWidth="xl" sx={{ 
          ...(DEBUG_MODE && {
            // DEBUG: CTA container
            border: '2px solid lime',
            position: 'relative',
            '&::before': {
              content: '"CTA CONTAINER"',
              position: 'absolute',
              top: 0,
              left: 0,
              background: 'lime',
              color: 'black',
              padding: '2px 8px',
              fontSize: '12px',
              zIndex: 10,
            }
          })
        }}>
          <Box textAlign="center">
            <Button
              variant="hero"
              size="large"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleCTAClick}
              sx={{ mb: 2 }}
            >
              {getCTAText()}
            </Button>
            <Box>
              <Link
                component="button"
                variant="body2"
                onClick={handleDemoClick}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  '&:hover': {
                    textDecoration: 'none',
                  },
                }}
              >
                Or, try a live demo below
              </Link>
            </Box>
          </Box>
        </Container>

        {/* Feature cards */}
        <Container maxWidth="xl" sx={{ 
          display: 'flex',
          justifyContent: 'center',
          ...(DEBUG_MODE && {
            // DEBUG: Feature cards container
            border: '2px solid orange',
            position: 'relative',
            '&::before': {
              content: '"FEATURE CARDS CONTAINER"',
              position: 'absolute',
              top: 0,
              left: 0,
              background: 'orange',
              color: 'black',
              padding: '2px 8px',
              fontSize: '12px',
              zIndex: 10,
            }
          })
        }}>
          <Box sx={{ maxWidth: '1200px', width: '100%' }}>
            <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={4}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      height: '100%',
                      width: '100%',
                      minHeight: '200px',
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: 48, 
                          color: studentTheme.primary.main,
                          mb: 2 
                        }} 
                      />
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Authentic HSC Questions
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Practice with confidence. Every question is from a HSC paper, vetted by expert tutors.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      height: '100%',
                      width: '100%',
                      minHeight: '200px',
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <BoltIcon 
                        sx={{ 
                          fontSize: 48, 
                          color: studentTheme.primary.main,
                          mb: 2 
                        }} 
                      />
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Generate Exams in Seconds
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Go from topic selection to a full PDF exam in under 30 seconds. Get straight to studying.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      height: '100%',
                      width: '100%',
                      minHeight: '200px',
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <GpsFixedIcon 
                        sx={{ 
                          fontSize: 48, 
                          color: studentTheme.primary.main,
                          mb: 2 
                        }} 
                      />
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Precise Practice
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Stop wasting time on what you already know. Select the topics and difficulty you really need.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
            </Grid>
          </Box>
        </Container>
      </Stack>

      {/* Demo section - outside of Stack for proper centering */}
      <Container maxWidth="xl" sx={{ 
        mt: 6, 
        mb: 8,
        ...(DEBUG_MODE && {
          // DEBUG: Demo container
          border: '2px solid red',
          position: 'relative',
          '&::before': {
            content: '"DEMO CONTAINER"',
            position: 'absolute',
            top: 0,
            left: 0,
            background: 'red',
            color: 'white',
            padding: '2px 8px',
            fontSize: '12px',
            zIndex: 10,
          }
        })
      }}>
        <Box 
          id="demo-section"
          sx={{ 
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
            ...(DEBUG_MODE && {
              // DEBUG: Demo wrapper box
              border: '2px solid pink',
            }),
          }}
        >
          {renderDemo()}
        </Box>
      </Container>
      
      {/* Email Capture Modal */}
      <EmailCaptureModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        source="hero"
        headlineVariant={headlineVariant}
        ctaVariant={ctaVariant}
        colorTheme={colorTheme}
      />
    </Box>
  );
};

export default HeroWithDemo;
